
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { required, email, maxLength, url } from '@vuelidate/validators';
import { estateAgencyService } from '@/estate-agency/EstateAgencyService';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import MapPosition from '@/shared/components/MapPosition.vue';
import Cropper from '@/shared/components/Cropper.vue';
import { Address } from '@/shared/models/Address';
import { Point } from '@/shared/models/Point';
import { UploadFile } from '@/shared/models/UploadFile';
import { deepCopy } from '@/utils';
import useVuelidate from '@vuelidate/core';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';

export default defineComponent({
  name: 'EstateAgencyProfileEdit',
  mixins: [Form],
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    MapPosition,
    ResizableImage,
    Cropper,
  },
  data() {
    return {
      initialPosition: null as null | Point,
      estateAgencyImage: {} as null | UploadFile,
      showEstateAgencyImageModal: false,
      form: {
        title: null as null | string,
        idNumber: null as null | string,
        taxIdNumber: null as null | string,
        vatIdNumber: null as null | string,
        email: null as null | string,
        phone: null as null | string,
        website: null as null | string,
        logo: null as null | string,
        address: null as null | string,
        street: null as null | string,
        city: null as null | string,
        zip: null as null | string,
        countryCode: 'sk',
        latitude: null as null | number,
        longitude: null as null | number,
        invoiceStreet: null as null | string,
        invoiceCity: null as null | string,
        invoiceZip: null as null | string,
        invoiceCountryCode: 'sk',
        phoneNumbers: [] as string[],
        description: null as null | string,
        backOfficeConsumerKey: null as null | string,
        backOfficeConsumerSecret: null as null | string,
      },
    };
  },
  validations() {
    return {
      form: {
        title: { required },
        idNumber: {
          required,
          maxLength: maxLength(20),
        },
        taxIdNumber: { maxLength: maxLength(20) },
        vatIdNumber: { maxLength: maxLength(20) },
        email: { required, email },
        phone: { required, phoneNumber: phoneNumber() },
        website: { url },
        address: { required },
        latitude: { required },
        longitude: { required },
        invoiceStreet: {},
        invoiceCity: {},
        invoiceZip: {},
        invoiceCountryCode: {},
        phoneNumbers: {},
        description: {},
        backOfficeConsumerKey: {},
        backOfficeConsumerSecret: {},
      },
    };
  },
  async created() {
    this.startLoading();
    try {
      const result = await estateAgencyService.getProfile();
      this.form = result.data;
      if (this.form.latitude && this.form.longitude) {
        this.initialPosition = { lat: this.form.latitude, lng: this.form.longitude };
      }
      if (this.form.phoneNumbers && this.form.phoneNumbers.length > 0) {
        this.form.phone = this.form.phoneNumbers[0];
        this.form.phoneNumbers.splice(0, 1);
      }
      if (result.data.logo) {
        this.estateAgencyImage = {} as UploadFile;
        this.estateAgencyImage.file = result.data.logo;
        this.estateAgencyImage.url = result.data.logoRelativeUrl;
        this.estateAgencyImage.isSuccess = true;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.endLoading();
    }
  },
  methods: {
    removePhoneNumber(index: number) {
      this.form.phoneNumbers.splice(index, 1);
    },
    async save() {
      if (!this.validate()) return;

      try {
        this.startLoading();
        const cloneForm = deepCopy(this.form);
        if (cloneForm.phone) {
          cloneForm.phoneNumbers.unshift(cloneForm.phone);
        }
        await estateAgencyService.updateProfile(cloneForm);
        this.success(false);
        this.$router.push({ name: 'EstateAgencyProfile' });
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.endLoading();
      }
    },
    async handleEstateAgencyImageUpload() {
      const ref = this.$refs.estateAgencyImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadEstateAgencyImage(file);
      ref.value = null;
    },
    async uploadEstateAgencyImage(file: any) {
      this.showEstateAgencyImageModal = false;
      this.estateAgencyImage = {} as UploadFile;
      this.estateAgencyImage.isUploading = true;
      this.estateAgencyImage.uploadPercentage = 0;
      this.estateAgencyImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await estateAgencyService.uploadLogo(formData, (progressEvent: any) => {
          if (this.estateAgencyImage) {
            this.estateAgencyImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgencyImage.file = result.data.logo;
        this.estateAgencyImage.url = result.data.url;
        this.estateAgencyImage.isSuccess = true;
        this.form.logo = result.data.logo;
      } catch (error) {
        this.estateAgencyImage.isError = true;
        this.estateAgencyImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgencyImage.text = errors[0].message;
      } finally {
        this.estateAgencyImage.isUploading = false;
      }
    },
    editEstateAgencyFile() {
      this.showEstateAgencyImageModal = true;
    },
    deleteEstateAgencyFile() {
      this.estateAgencyImage = {} as UploadFile;
      this.form.logo = null;
    },
    onAddressChanged(address: Address) {
      if (address) {
        this.form.street = address.streetNumber ? address.street + ' ' + address.streetNumber : address.street;
        this.form.city = address.city;
        this.form.zip = address.zip;
        this.form.countryCode = address.countryCode;
        this.form.latitude = address.latitude;
        this.form.longitude = address.longitude;
        this.form.address = address.address;
      } else {
        this.form.street = '';
        this.form.city = '';
        this.form.zip = '';
        this.form.countryCode = '';
        this.form.latitude = null;
        this.form.longitude = null;
        this.form.address = null;
      }
      const validationControl = (this.v$.form as any).address;
      if (validationControl) {
        validationControl.$touch();
      }
    },
  },
});
